import React, { Component, cloneElement } from 'react';
import Header from '../../components/Header';
import Description from '../../components/Description';
import FormLabel from '../../components/FormLabel';
import FormField from '../../components/FormField';
import AutoComplete from '../../components/AutoComplete';
import StickyHeader from '../../components/StickyHeader/StickyHeader';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import { updateCompany, validateCompany, writeCompanySettings } from 'redux/modules/companies';
import i18n from 'i18n/i18n';
import _ from 'lodash';
import Select from '@material-ui/core/Select';
import SelectArrow from '@material-ui/icons/KeyboardArrowDown';
import FormHelperText from '@material-ui/core/FormHelperText';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import ValidationSchema from './CompanyProfileSchema';
import { isAdmin } from 'utils/state';
import { history, setConfirmationFunction } from 'redux/store';
import { showModal } from 'redux/modules/modal';
import CenteredContent from 'components/CenteredContent/CenteredContent';
import { typingDebounceTimeout } from '../../theme/uiConfig';
import { LOCALES_MAPPING } from 'utils/language';

const localize = i18n.loc.localize;

const styles = theme => {
	return {
		button: {
			width: 200
		},
		bottomBorder: {
			paddingBottom: 3 * theme.spacing.unit
		},
		details: {
			paddingBottom: theme.spacing.unit * 4
		},
		detailsContainer: {
			marginTop: theme.spacing.unit * 2,
		},
		field: {
			width: 240,
			margin: '0 0 5px 0',
			'&:focus': {
				outline: 'none'
			}
		},
		'root': {
			'&$error': {
				marginTop: 10,
				marginBottom: 15,
				fontSize: '0.8rem'
			}
		},
		'error': {},
		fieldDescription: {
			marginTop: 5,
			width: 300,
			marginLeft: 18,
			'& sup': {
				marginLeft: -10,
				marginRight: 3
			}
		},
		errorMessage:  {
			color: theme.palette.error.main,
			opacity: 1,
			marginTop: 15,
			marginBottom: 0,
			width: '100%'
		}
	};
};

const DEFAULT_INDUSTRY = {industryId: 1000, industry: 'Other'};
const onLeaveMessage = localize('leave-page-confirmation');
import ERRORS from '../../errors/errorids';
const errors = {
	[ERRORS.OFFLINE] : localize('subscriptions-error-offline'),
	[ERRORS.API_REQUEST_LIMIT_EXCEEDED] : localize('error-cb-api-rate-limit-reached')

};

@withStyles(styles)
class CompanyProfile extends Component {

	constructor(props) {
		super();

		const companyData = _.pick(props.company, Object.keys(ValidationSchema));
		companyData.language = _.get(props.company, 'settings.languageLocaleCode') || '';

		this.state = {
			values: companyData,
			touched: {},
			errors: {},
			companySizes: [],
			companyLanguages: LOCALES_MAPPING.sort((a, b) => {
				const languageA = a.language;
				const languageB = b.language;
				if (languageA < languageB) {
					return -1;
				}
				if (languageA > languageB) {
					return 1;
				}
				return 0;
			}),
			companyIndustries: [],
			companyCountries: []
		};
		this.onBeforeUnload = this.onBeforeUnload.bind(this);
		this.validateCompany = _.debounce(props.validateCompany, typingDebounceTimeout);

	}

	componentDidMount() {
		setConfirmationFunction((message, callback) => {
			this.doIfCanLeave(callback);
		});
		this.historyUnblock = history.block('');

		window.addEventListener('beforeunload', this.onBeforeUnload);
	}

	componentWillUnmount() {
		this.historyUnblock();
		setConfirmationFunction(null);

		window.removeEventListener('beforeunload', this.onBeforeUnload);
	}

	onChange(event, name) {
		this.setFieldState(name, event.target.value);
	}

	setFieldState(fieldName, value) {
		let newState = _.cloneDeep(this.state);
		newState.values[fieldName] = value;

		this.setState(newState, this.validateAll);
	}

	setIndustryFieldState(fieldName = 'industry', value) {
		let newState = _.cloneDeep(this.state);
		newState.values[fieldName] = value;

		const validIndustry = this.props.companyIndustries.find(industry => industry.displayName === value);
		if (validIndustry) {
			newState.values.industryId = validIndustry.industryId;
		}
		this.setState(newState, this.validateAll);
	}

	onBlur(event, name) {
		let newState = _.cloneDeep(this.state);
		newState.touched[name] = true;
		this.setState(newState, this.validateAll);
	}

	validateAll() {
		let errors = {};
		for (let field in ValidationSchema) {
			let validate = ValidationSchema[field];
			if (validate) {
				let error = validate(this.state.values[field], this.state, this.props);
				if (error) {
					errors[field] = error;
				}
			}
		}
		this.setState({ errors });
	}

	hasUnsavedData() {
		const changed = this.getChangedData();
		return !_.isEmpty(changed);
	}

	doIfCanLeave(callback) {
		if (!this.hasUnsavedData()) {
			callback(true);
			return;
		}

		this.props.showModal('CONFIRMATION_MODAL', {
			message: onLeaveMessage,
			buttons: [
				<Button
					key='button-cancel'
					variant='outlined'
					color='primary'
					data-tid="button-company-profile-company-stay"
					onClick={() => {callback(false);}}
				>
					{localize('stay-on-page')}
				</Button>,
				<Button
					key='button-confirm'
					data-tid="button-company-profile-company-leave"
					variant='contained'
					color='primary'
					onClick={() => {callback(true);}}
				>
					{localize('leave-page')}
				</Button>
			]
		});
	}

	onBeforeUnload(e) {
		if (this.hasUnsavedData()) {
			let dialogText = onLeaveMessage;
			e.returnValue = dialogText;
			return dialogText;
		}
	}

	field(name, placeholder) {
		const { classes, isAdmin } = this.props;
		const errorClasses = { root: classes.root, error: classes.error };
		return (
			<FormField
				value={this.state.values[name]}
				className={classes.field}
				data-tid={`form-company-profile-${name}`}
				onChange={event => this.onChange(event, name)}
				onBlur={event => this.onBlur(event, name)}
				id={name}
				placeholder={placeholder}
				margin="normal"
				helperText={this.state.errors[name]}
				error={!!this.state.errors[name]}
				FormHelperTextProps={{ classes: errorClasses }}
				disabled={!isAdmin}
			/>
		);
	}

	fieldTeamName(name, placeholder) {
		return cloneElement(this.field(name, placeholder), {
			onChange:(event) => {
				this.onChange(event, name);
				event.target.value !== '' && event.target.value !== this.props.company.displayName &&
					this.validateCompany(event.target.value);
			},
			helperText: this.state.errors[name] ||
				!this.props.isNameValid && localize('company-profile-team-name-taken'),
			error: this.state.errors[name] || !this.props.isNameValid
		});
	}

	updateCompany(companyId, changed) {
		//[P9052-11080] Use industryId over existing industry information, default is other
		if (changed.industry === '') {
			changed = {...changed, ...DEFAULT_INDUSTRY};
		}

		if (changed.language) {
			this.saveCompanyLanguage();
			delete changed.language;
		}

		if (!_.isEmpty(changed)) {
			this.props.updateCompany(companyId, changed);
		}
	}

	saveCompanyLanguage() {
		this.props.writeCompanySettings({ languageLocaleCode: this.state.values.language });
	}

	getChangedData() {
		return _.pickBy(
				_.omit(this.state.values, ['industry']), 
				(value, field) => (field !== 'language' && this.props.company[field] !== value) || 
				(field === 'language' && value !== _.get(this.props.company, 'settings.languageLocaleCode') && value !== ''));
	}

	render() {
		const { classes, company, companyIsUpdating, isAdmin, isNameValid } = this.props;

		const readOnly = !isAdmin;

		const changed = this.getChangedData();

		const canSave = Object.keys(changed).length && !Object.keys(this.state.errors).length
			&& !companyIsUpdating && isNameValid;

		const errorClasses = { root: classes.root, error: classes.error };

		//[P9052-11080] Use industryId over existing industry information
		let initialIndustry = '';
		if (this.props.company.industryId && this.state.values.industry === null) {
			initialIndustry = (this.props.companyIndustries.find(industry =>
				industry.industryId === this.props.company.industryId)|| {}).displayName || '';
		}

		let saveButton = (<Button
			variant='contained'
			color='primary'
			data-tid="button-company-profile-save"
			className={classes.button}
			onClick={() => this.updateCompany(company.id, changed)}
			disabled={!canSave || readOnly}>
			{localize('save')}
		</Button>);

		const renderErrorMessage = () => {
			if (!this.props.companies.companyIsUpdating && this.props.companies.companyUpdatingErrorId ||
				_.get(this.props.company, 'companySettingsUpdatingErrorId')
			) {

				const errorMessage = 
				_.get(this.props.company, 'companySettingsUpdatingErrorId')
					?
					errors[this.props.company.companySettingsUpdatingErrorId] || localize('company-profile-settings-error')
					:
					errors[this.props.companies.companyUpdatingErrorId] || localize('subscriptions-error-unknown');

				return (
					<Typography
						className={classes.errorMessage}
						variant="body2"
						dangerouslySetInnerHTML={{ __html: errorMessage }} />
				);
			}
		};

		return (<CenteredContent>
			<StickyHeader>
				<Header>{localize('company-profile-header')}</Header>
				<Description>
					{localize(readOnly ? 'company-profile-description-read-only' : 'company-profile-description')}
				</Description>
				<Grid container className={classes.details}>
					{saveButton}
					{renderErrorMessage()}
				</Grid>
			</StickyHeader>
			<Grid container className={classNames(classes.detailsContainer, classes.bottomBorder)}>
				<Grid container direction="column">
					<FormLabel title={localize('required-field')} required>
						{localize('company-profile-team-name')}
					</FormLabel>
					{ this.fieldTeamName('displayName', localize('company-profile-team-name-placeholder'))}
				</Grid>
				<Grid container direction="column">
					<FormLabel required>{localize('company-profile-language')}</FormLabel>
					<Select
						data-tid="select-company-profile-company-language"
						className={classes.field}
						value={this.state.values.language}
						name="language"
						displayEmpty
						disableUnderline
						IconComponent={SelectArrow}
						onChange={(event) => {
							let newState = _.cloneDeep(this.state);
							newState.values['language'] = event.target.value;
							this.setState(newState);
						}}
						disabled={readOnly}
					>
						<MenuItem value="" disabled>{localize('company-profile-language-selector')}</MenuItem>
						{this.state.companyLanguages.map((option) => {
							return (<MenuItem
								key={option.locale}
								value={option.locale}
								data-tid={`select-company-profile-company-language-${option.locale}`}>
								{option.language} - {option.country}
							</MenuItem>);
						})}
					</Select>
				</Grid>
				<Grid container direction="column">
					<FormLabel title={localize('required-field')} required>
						{localize('company-profile-email')}
					</FormLabel>
					{ this.field('email', localize('company-profile-email-placeholder'))}
					<Typography className={classes.fieldDescription} variant="body2">
						<sup>*</sup>{localize('company-profile-email-description', company.displayName)}
					</Typography>
				</Grid>
				<Grid container direction="column">
					<FormLabel>{localize('company-profile-address')}</FormLabel>
					{ this.field('address1', localize('company-profile-address1-placeholder'))}
					{ this.field('address2', localize('company-profile-address2-placeholder'))}
					{ this.field('city', localize('company-profile-city-placeholder'))}
					{ this.field('region', localize('company-profile-region-placeholder'))}
					{ this.field('postcode', localize('company-profile-postcode-placeholder'))}
					<AutoComplete
						inputProps={{
							value: this.state.values.country,
							onChange: (event, { newValue }) => {
								this.setFieldState('country', newValue);
							},
							onBlur: (event) => {
								this.onBlur(event, 'country');
							},
							placeholder: localize('company-profile-country-placeholder'),
							disabled: readOnly
						}}
						suggestions={this.props.companyCountries}
					/>
					{ this.state.errors['country'] &&
						<FormHelperText error={true} classes={errorClasses}>
							{this.state.errors['country']}
						</FormHelperText>
					}
				</Grid>
				<Grid container direction="column">
					<FormLabel>{localize('company-profile-phone')}</FormLabel>
					{ this.field('phone', localize('company-profile-phone-placeholder'))}
				</Grid>
				<Grid container direction="column">
					<FormLabel>{localize('company-profile-website')}</FormLabel>
					{ this.field('website', localize('company-profile-website-placeholder'))}
				</Grid>
				<Grid container direction="column">
					<FormLabel>{localize('company-profile-industry')}</FormLabel>
					<Select
						data-tid="select-company-profile-company-industry"
						className={classes.field}
						value={this.state.values.industry || initialIndustry}
						name="industry"
						displayEmpty
						disableUnderline
						IconComponent={SelectArrow}
						onChange={(event) => {
							this.setIndustryFieldState('industry', event.target.value);
						}}
						disabled={readOnly}
					>
						<MenuItem value="" disabled>{localize('company-profile-industry-selector')}</MenuItem>
						{this.props.companyIndustries.map((industry) => {
							return (<MenuItem
								key={industry.displayName}
								value={industry.displayName}
								data-tid={`select-company-profile-company-industry-${industry.displayName}`}>
								{industry.displayName}
							</MenuItem>);
						})}
					</Select>
				</Grid>
				<Grid container direction="column">
					<FormLabel>{localize('company-profile-size')}</FormLabel>
					<Select
						data-tid="select-company-profile-company-size"
						className={classes.field}
						value={this.state.values.size}
						name="size"
						displayEmpty
						disableUnderline
						IconComponent={SelectArrow}
						onChange={(event) => {
							let newState = _.cloneDeep(this.state);
							newState.values['size'] = event.target.value;
							this.setState(newState);
						}}
						disabled={readOnly}
					>
						<MenuItem value="" disabled>{localize('company-profile-size-selector')}</MenuItem>
						{this.props.companySizes.map((option) => {
							return (<MenuItem
								key={option}
								value={option}
								data-tid={`select-company-profile-company-size-${option}`}>
								{option}
							</MenuItem>);
						})}
					</Select>
				</Grid>
			</Grid>
		</CenteredContent>);
	}

}

const mapStateToProps = state => ({
	companies: state.companies,
	company: state.companies.rawDataById[state.companies.selectedId],
	companyIsUpdating: state.companies.companyIsUpdating,
	companySizes: state.companies.sizes,
	companyIndustries: state.companies.industries,
	companyCountries: state.companies.countries,
	isAdmin: isAdmin(state),
	isNameValid: state.companies.valid
});

const mapDispatchToProps = dispatch => bindActionCreators({
	writeCompanySettings,
	validateCompany,
	updateCompany,
	showModal
}, dispatch);

CompanyProfile.propTypes = {
	classes: PropTypes.object,
	cloudFolders: PropTypes.object,
	company: PropTypes.object,
	validateCompany: PropTypes.func.isRequired,
	updateCompany: PropTypes.func.isRequired,
	companyIsUpdating: PropTypes.bool.isRequired,
	companySizes: PropTypes.array.isRequired,
	companyIndustries: PropTypes.array.isRequired,
	companyCountries: PropTypes.array.isRequired,
	isAdmin: PropTypes.bool,
	showModal: PropTypes.func,
	isNameValid: PropTypes.bool,
	companies: PropTypes.object,
	writeCompanySettings: PropTypes.func
};

CompanyProfile.defaultProps = {
	company: {}
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(CompanyProfile);



// WEBPACK FOOTER //
// ./src/containers/CompanyProfile/CompanyProfile.js